import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import {
  IAirtableSyncPayloadDataModel,
  IEstablishedSyncDataModel,
  ISyncedColumnDataModel,
} from '../../../../data-models/airtable-sync.data-model';
import {
  createAirtableSync,
  deleteEstablishedColumnSync,
  deleteEstablishedSync,
  fetchAirtableEntities,
  fetchConnectedBases,
  fetchEstablishedSyncs,
  fetchTablesForBase,
} from '../../../../services/queries/MaggieIntegrationsQueries';
import { getErrorMessage } from '../../../../services/queryHelpers';

import { useToastMessageState } from '../../../../components/ToastMessage/ToastMessageProvider';
import { selectedTableState } from '../../state/ConfigurationState';
import {
  airTableBasesState,
  airTableBasesStateLoadable,
  airTableTablesState,
  airtableEntitiesState,
  establishedSyncsState,
} from '../../state/IntegrationsState';

export function useAirtableActions() {
  const { pushErrorToast, pushInfoToast } = useToastMessageState();

  const refetchSyncData = useAtomCallback(
    useCallback(async (_get, set) => {
      const syncs = await fetchEstablishedSyncs();
      set(establishedSyncsState, syncs);
    }, [])
  );

  const refetchConfigData = useAtomCallback(
    useCallback(async (get, set, baseId?: string) => {
      const selectedTable = get(selectedTableState);
      if (!selectedTable && !baseId) {
        return;
      }
      const selectedBaseId = baseId ?? selectedTable!.baseId;

      const [entities, bases, tables] = await Promise.all([
        fetchAirtableEntities(),
        fetchConnectedBases(),
        fetchTablesForBase(selectedBaseId),
      ]);
      set(airtableEntitiesState, entities);
      set(airTableBasesState, bases);
      set(airTableTablesState(selectedBaseId), tables);
    }, [])
  );

  const isBasesLoaded = useAtomCallback(
    useCallback((get) => get(airTableBasesStateLoadable).state === 'hasData', [])
  );

  const disconnectSync = useCallback(
    async (sync: IEstablishedSyncDataModel) => {
      try {
        await deleteEstablishedSync(sync.id);

        refetchSyncData();
        if (await isBasesLoaded()) {
          refetchConfigData(sync.airtableBaseId);
        }

        pushInfoToast({
          message: `Synchronization with Airtable table "${sync.airtableTableName}" has been disconnected.`,
        });
      } catch (err) {
        console.error(err);
        const message = getErrorMessage(err, 'Failed to disconnect sync');
        pushErrorToast({ message });
      }
    },
    [isBasesLoaded, pushErrorToast, pushInfoToast, refetchConfigData, refetchSyncData]
  );

  const disconnectSyncColumn = useCallback(
    async (column: ISyncedColumnDataModel, sync: IEstablishedSyncDataModel) => {
      try {
        const disconnected = await deleteEstablishedColumnSync(column.id);
        refetchSyncData();
        if (await isBasesLoaded()) {
          refetchConfigData(sync.airtableBaseId);
        }

        pushInfoToast({
          message: `Synchronization with Airtable column "${column.airtableColumnName}" has been disconnected.`,
        });
        return disconnected;
      } catch (err) {
        console.error(err);
        const message = getErrorMessage(err, 'Failed to disconnect sync column');
        pushErrorToast({ message });
      }
    },
    [isBasesLoaded, pushErrorToast, pushInfoToast, refetchConfigData, refetchSyncData]
  );

  const createSync = useCallback(
    async (payload: IAirtableSyncPayloadDataModel) => {
      try {
        await createAirtableSync(payload);
        refetchConfigData();
        refetchSyncData();
      } catch (e) {
        console.error(e);
        const message = getErrorMessage(e, 'Failed to match columns');
        pushErrorToast({ message });
      }
    },
    [pushErrorToast, refetchConfigData, refetchSyncData]
  );

  return { createSync, disconnectSync, disconnectSyncColumn };
}
